import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Share2, 
  Edit3, 
  Check, 
  X, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  Pill,
  AlertCircle,
  Link2,
  Copy,
  Clock,
  ChevronRight,
  RefreshCw
} from 'lucide-react';

const SEVERITY_COLORS = {
  High: 'bg-red-500/20 text-red-400 border-red-500/30',
  Moderate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Low: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const DoctorSummary = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingConcerns, setEditingConcerns] = useState(false);
  const [editingSymptoms, setEditingSymptoms] = useState(false);
  const [concerns, setConcerns] = useState('');
  const [symptomsText, setSymptomsText] = useState('');
  const [shareLink, setShareLink] = useState(null);
  
  // Generate form state
  const [formData, setFormData] = useState({
    includeName: false,
    patientName: '',
    concerns: '',
    symptomsText: '',
    periodDays: 30
  });

  const fetchLatestSummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/summary/latest');
      if (response.data) {
        setSummary(response.data);
        setConcerns(response.data.concerns || '');
        setSymptomsText(response.data.symptoms_text || '');
      }
    } catch (error) {
      // No summary yet is fine
      console.log('No existing summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatestSummary();
  }, [fetchLatestSummary]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      setGenerating(true);
      const response = await api.post('/summary/generate', {
        include_name: formData.includeName,
        patient_name: formData.includeName ? formData.patientName : null,
        concerns: formData.concerns,
        symptoms_text: formData.symptomsText,
        period_days: formData.periodDays
      });
      setSummary(response.data);
      setConcerns(response.data.concerns || '');
      setSymptomsText(response.data.symptoms_text || '');
      setShowGenerateModal(false);
      toast.success('Summary generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateConcerns = async () => {
    try {
      await api.put(`/summary/${summary.id}/concerns?concerns=${encodeURIComponent(concerns)}`);
      setSummary({ ...summary, concerns });
      setEditingConcerns(false);
      toast.success('Concerns updated');
    } catch (error) {
      toast.error('Failed to update concerns');
    }
  };

  const handleUpdateSymptoms = async () => {
    try {
      await api.put(`/summary/${summary.id}/symptoms?symptoms_text=${encodeURIComponent(symptomsText)}`);
      setSummary({ ...summary, symptoms_text: symptomsText });
      setEditingSymptoms(false);
      toast.success('Symptoms updated');
    } catch (error) {
      toast.error('Failed to update symptoms');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/summary/${summary.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `doctor_summary_${summary.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleCreateShareLink = async () => {
    try {
      const response = await api.post(`/summary/${summary.id}/share`);
      const fullUrl = `${window.location.origin}${response.data.share_url}`;
      setShareLink({
        url: fullUrl,
        expires: response.data.expires_at
      });
      toast.success('Share link created');
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink.url);
    toast.success('Link copied to clipboard');
  };

  const handleRevokeLink = async () => {
    try {
      await api.delete(`/summary/${summary.id}/share`);
      setShareLink(null);
      setSummary({ ...summary, share_token: null, share_url: null });
      toast.success('Share link revoked');
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  // Mini mood chart component
  const MoodChart = ({ scores, dates }) => {
    if (!scores || scores.length === 0) {
      return <p className="text-muted-foreground text-sm">No mood data recorded</p>;
    }
    
    const maxScore = 5;
    const chartHeight = 60;
    const pointSpacing = Math.min(20, 280 / scores.length);
    
    return (
      <div className="relative h-20">
        <svg width="100%" height={chartHeight} className="overflow-visible">
          {/* Grid lines */}
          {[1, 2, 3, 4, 5].map(level => (
            <line
              key={level}
              x1="0"
              y1={chartHeight - (level / maxScore) * chartHeight}
              x2="100%"
              y2={chartHeight - (level / maxScore) * chartHeight}
              stroke="currentColor"
              strokeOpacity="0.1"
            />
          ))}
          {/* Line chart */}
          <polyline
            fill="none"
            stroke="url(#moodGradient)"
            strokeWidth="2"
            points={scores.map((score, i) => 
              `${i * pointSpacing + 10},${chartHeight - (score / maxScore) * chartHeight}`
            ).join(' ')}
          />
          {/* Gradient definition */}
          <defs>
            <linearGradient id="moodGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          {/* Data points */}
          {scores.slice(0, 15).map((score, i) => (
            <circle
              key={i}
              cx={i * pointSpacing + 10}
              cy={chartHeight - (score / maxScore) * chartHeight}
              r="3"
              fill="#8b5cf6"
            />
          ))}
        </svg>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="doctor-summary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Doctor-Ready Summary</h1>
          <p className="text-muted-foreground">One-click summary for your healthcare appointments</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:opacity-90 transition-opacity font-medium"
          data-testid="generate-summary-button"
        >
          <RefreshCw className="w-5 h-5" />
          {summary ? 'Generate New' : 'Generate Summary'}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : !summary ? (
        <div className="bg-card rounded-2xl border border-border p-12 text-center">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">No Summary Yet</h2>
          <p className="text-muted-foreground mb-6">
            Generate a comprehensive summary of your recent health data to share with your doctor
          </p>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-6 py-3 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity"
          >
            Generate Your First Summary
          </button>
        </div>
      ) : (
        <>
          {/* Summary Card */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            {/* Summary Header */}
            <div className="p-6 border-b border-border bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    {summary.patient_name ? `Summary for ${summary.patient_name}` : 'Your Health Summary'}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {summary.period_start} to {summary.period_end}
                  </p>
                </div>
                <span className="px-3 py-1 bg-muted rounded-full text-xs text-muted-foreground">
                  Generated {new Date(summary.generated_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Summary Content */}
            <div className="p-6 space-y-6">
              {/* Mood Trend Section */}
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-semibold text-foreground">Mood Trend</h3>
                </div>
                <MoodChart scores={summary.mood_trend.scores} dates={summary.mood_trend.dates} />
                <p className="text-sm text-muted-foreground mt-3 italic">
                  "{summary.mood_trend.insight}"
                </p>
              </div>

              {/* Key Symptoms - Editable */}
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                    <h3 className="font-semibold text-foreground">Key Symptoms</h3>
                    <span className="text-xs text-muted-foreground">(In Your Own Words)</span>
                  </div>
                  {!editingSymptoms && (
                    <button
                      onClick={() => setEditingSymptoms(true)}
                      className="text-sm text-primary hover:underline"
                      data-testid="edit-symptoms-button"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingSymptoms ? (
                  <div className="space-y-3">
                    <textarea
                      value={symptomsText}
                      onChange={(e) => setSymptomsText(e.target.value)}
                      placeholder="Describe any symptoms you've been experiencing, such as sleep issues, anxiety, fatigue, appetite changes, etc."
                      className="w-full p-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      rows={4}
                      data-testid="symptoms-textarea"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateSymptoms}
                        className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm"
                      >
                        <Check className="w-4 h-4" /> Save
                      </button>
                      <button
                        onClick={() => {
                          setSymptomsText(summary.symptoms_text || '');
                          setEditingSymptoms(false);
                        }}
                        className="flex items-center gap-1 px-4 py-2 bg-muted text-foreground rounded-lg text-sm"
                      >
                        <X className="w-4 h-4" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-foreground italic">
                    {summary.symptoms_text ? `"${summary.symptoms_text}"` : 'No symptoms described yet. Click Edit to add your symptoms.'}
                  </p>
                )}
              </div>

              {/* Medication Adherence */}
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Pill className="w-5 h-5 text-purple-400" />
                  <h3 className="font-semibold text-foreground">Medication Adherence</h3>
                </div>
                {summary.medications.length > 0 ? (
                  <div className="space-y-2">
                    {summary.medications.map((med, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-background rounded-lg">
                        <div>
                          <span className="font-medium text-foreground">{med.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">{med.dosage}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-medium ${
                            med.adherence_percentage >= 80 ? 'text-green-400' : 
                            med.adherence_percentage >= 60 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {med.adherence_percentage}%
                          </span>
                          {med.missed_doses > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({med.missed_doses} missed)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No active medications recorded</p>
                )}
              </div>

              {/* Patient Concerns */}
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Edit3 className="w-5 h-5 text-blue-400" />
                    <h3 className="font-semibold text-foreground">Your Concerns</h3>
                    <span className="text-xs text-muted-foreground">(In Your Own Words)</span>
                  </div>
                  {!editingConcerns && (
                    <button
                      onClick={() => setEditingConcerns(true)}
                      className="text-sm text-primary hover:underline"
                      data-testid="edit-concerns-button"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingConcerns ? (
                  <div className="space-y-3">
                    <textarea
                      value={concerns}
                      onChange={(e) => setConcerns(e.target.value)}
                      placeholder="Describe any concerns, questions, or topics you'd like to discuss with your doctor..."
                      className="w-full p-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      rows={4}
                      data-testid="concerns-textarea"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateConcerns}
                        className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm"
                      >
                        <Check className="w-4 h-4" /> Save
                      </button>
                      <button
                        onClick={() => {
                          setConcerns(summary.concerns || '');
                          setEditingConcerns(false);
                        }}
                        className="flex items-center gap-1 px-4 py-2 bg-muted text-foreground rounded-lg text-sm"
                      >
                        <X className="w-4 h-4" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-foreground italic">
                    {summary.concerns ? `"${summary.concerns}"` : 'No concerns added yet. Click Edit to add your questions or topics.'}
                  </p>
                )}
              </div>

              {/* Disclaimer */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-xs text-yellow-400">
                  <strong>Disclaimer:</strong> This summary is informational and is intended to support professional medical consultation. It does not provide diagnosis or treatment recommendations.
                </p>
              </div>
            </div>

            {/* Actions Footer */}
            <div className="p-6 border-t border-border bg-muted/30">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
                  data-testid="download-pdf-button"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={handleCreateShareLink}
                  className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/70 transition-colors"
                  data-testid="create-share-link-button"
                >
                  <Share2 className="w-4 h-4" />
                  Create Share Link
                </button>
              </div>

              {/* Share Link Display */}
              {(shareLink || summary.share_url) && (
                <div className="mt-4 p-4 bg-background border border-border rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-foreground">Shareable Link (Read-Only)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareLink?.url || `${window.location.origin}${summary.share_url}`}
                      readOnly
                      className="flex-1 px-3 py-2 bg-muted border border-border rounded-lg text-sm text-muted-foreground"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="p-2 bg-primary text-white rounded-lg"
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleRevokeLink}
                      className="p-2 bg-red-500/20 text-red-400 rounded-lg"
                      title="Revoke link"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {shareLink?.expires && (
                    <p className="text-xs text-muted-foreground mt-2">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Expires: {new Date(shareLink.expires).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="generate-modal">
          <div className="bg-card rounded-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Generate Summary</h2>
                <button onClick={() => setShowGenerateModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleGenerate} className="p-6 space-y-4">
              {/* Period Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Time Period</label>
                <select
                  value={formData.periodDays}
                  onChange={(e) => setFormData({ ...formData, periodDays: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>

              {/* Include Name Toggle */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                <div>
                  <p className="font-medium text-foreground">Include Patient Name</p>
                  <p className="text-xs text-muted-foreground">Optional - for personalized summary</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.includeName}
                    onChange={(e) => setFormData({ ...formData, includeName: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:bg-primary transition-colors"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
                </label>
              </div>

              {formData.includeName && (
                <input
                  type="text"
                  value={formData.patientName}
                  onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              )}

              {/* Key Symptoms Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Key Symptoms <span className="text-muted-foreground">(Describe in your own words)</span>
                </label>
                <textarea
                  value={formData.symptomsText}
                  onChange={(e) => setFormData({ ...formData, symptomsText: e.target.value })}
                  placeholder="Describe any symptoms: sleep issues, anxiety, fatigue, appetite changes, mood swings, etc."
                  rows={3}
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  data-testid="symptoms-input"
                />
              </div>

              {/* Initial Concerns */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Your Concerns <span className="text-muted-foreground">(Optional)</span>
                </label>
                <textarea
                  value={formData.concerns}
                  onChange={(e) => setFormData({ ...formData, concerns: e.target.value })}
                  placeholder="Any specific concerns or questions for your doctor?"
                  rows={3}
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={generating}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-generate-button"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    Generate Summary
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export { DoctorSummary };